// src/components/common/OrderDetailsSidebar/OrderDetailsSidebar.jsx
import React from "react";
import OrderItemsList from "../OrderItemList/OrderItemList";
import useCurrency from "../../../hooks/useCurrency";
import { useLanguage } from "../../../context/LanguageContext";
import { getTranslation, getStatusTranslation } from "./translations";
import "./OrderDetailsSidebar.css";
import { useCart } from "../../../context/CartContext";
import { useNavigate } from "react-router-dom";

const OrderDetailsSidebar = ({
  selectedOrder,
  showDetailsSidebar,
  closeDetailsSidebar,
  formatDate,
  getStatusClass,
  formatPaymentMethod,
  getCustomerName,
}) => {
  const { toggleCart } = useCart();
  const onClose = () => toggleCart(false);
  const navigate = useNavigate();
  // Use centralized currency formatting
  const { formatDisplay, getCurrency } = useCurrency();

  // Get language context for translations
  const { language } = useLanguage();

  // Helper function to get translations
  const t = (key) => getTranslation(language, key);

  // Use the new formatter, fallback to legacy if needed
  const currencyFormatter = formatDisplay;
  if (!showDetailsSidebar || !selectedOrder) return null;

  // Helper function to get promo code display text
  const getPromoCodeText = () => {
    if (!selectedOrder.promoCode) return "";

    // Handle both object and string formats for backwards compatibility
    if (
      typeof selectedOrder.promoCode === "object" &&
      selectedOrder.promoCode.code
    ) {
      return selectedOrder.promoCode.code;
    } else if (typeof selectedOrder.promoCode === "string") {
      return selectedOrder.promoCode;
    }

    return "";
  };
  const handleItemClick = (item) => {
    // Close the cart panel first
    onClose();

    // Navigate to item detail page using the product ID
    // You can modify this to use a slug if your products have slugs
    navigate(`/product/${item.productId || item.id}`);
  };

  // Helper function to get discount amount from either location
  const getDiscountAmount = () => {
    // First try the direct discountAmount field
    if (selectedOrder.discountAmount) {
      return selectedOrder.discountAmount;
    }

    // Then try from the promoCode object
    if (
      selectedOrder.promoCode &&
      typeof selectedOrder.promoCode === "object" &&
      selectedOrder.promoCode.discountAmount
    ) {
      return selectedOrder.promoCode.discountAmount;
    }

    return 0;
  };

  const promoCodeText = getPromoCodeText();
  const discountAmount = getDiscountAmount();

  return (
    <div
      className="order-details-sidebar-overlay"
      onClick={closeDetailsSidebar}
    >
      <div
        className="order-details-sidebar"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-sidebar" onClick={closeDetailsSidebar}>
          ×
        </button>

        <div className="sidebar-header">
          <h2>{t("orderDetails")}</h2>
        </div>

        <div className="sidebar-content">
          <div className="order-basic-info">
            <div className="info-row">
              <span className="info-label">
                {selectedOrder.orderNumber ? t("orderNumber") : t("orderID")}:
              </span>
              <span className="info-value">
                {selectedOrder.orderNumber
                  ? `#${selectedOrder.orderNumber}`
                  : `#${selectedOrder.orderId || selectedOrder.id}`}
              </span>
            </div>

            {selectedOrder.orderNumber && (
              <div className="info-row">
                <span className="info-label">{t("orderID")}:</span>
                <span className="info-value">
                  {selectedOrder.orderId || selectedOrder.id}
                </span>
              </div>
            )}

            <div className="info-row">
              <span className="info-label">{t("date")}:</span>
              <span className="info-value">
                {formatDate(selectedOrder.createdAt)}
              </span>
            </div>

            <div className="info-row">
              <span className="info-label">{t("customer")}:</span>
              <span className="info-value">
                {getCustomerName(selectedOrder)}
              </span>
            </div>

            <div className="info-row">
              <span className="info-label">{t("paymentMethod")}:</span>
              <span className="info-value">
                {formatPaymentMethod(
                  selectedOrder.paymentMethod ||
                    selectedOrder.details?.paymentMethod
                )}
              </span>
            </div>

            <div className="info-row">
              <span className="info-label">{t("paymentStatus")}:</span>
              <span className="info-value">
                {formatPaymentMethod(selectedOrder.paymentStatus || "N/A")}
              </span>
            </div>
          </div>

          <div className="order-items-section">
            <h3>{t("orderItems")}</h3>
            <OrderItemsList
              items={selectedOrder.items || selectedOrder.details?.items || []}
              order={selectedOrder}
              currency={selectedOrder.currency || getCurrency()}
              formatCurrency={(amount) => parseFloat(amount || 0).toFixed(2)} // Pass number formatter to OrderItemsList
              variant="sidebar"
              showQuantity={true}
              showVariants={true}
              showItemTotal={true}
              showDiscountInfo={false}
              onItemClick={handleItemClick}
              className="order-items-sidebar"
              disableRTL={true}
            />
          </div>

          <div className="order-totals-section">
            <div className="totals-row">
              <span>{t("subtotal")}</span>
              <span>{currencyFormatter(selectedOrder.subtotal)}</span>
            </div>

            {selectedOrder.shipping && (
              <div className="totals-row">
                <span>
                  {t("shipping")}{" "}
                  {selectedOrder.shipping.method
                    ? `(${selectedOrder.shipping.method})`
                    : ""}
                </span>
                <span>{currencyFormatter(selectedOrder.shipping.cost)}</span>
              </div>
            )}

            {selectedOrder.tax !== undefined && (
              <div className="totals-row">
                <span>{t("tax")}</span>
                <span>{currencyFormatter(selectedOrder.tax?.amount)}</span>
              </div>
            )}

            {/* handle promo code discount display */}
            {discountAmount && discountAmount > 0 && (
              <div className="totals-row discount-row">
                <span>
                  {t("discount")}
                  {promoCodeText && ` (${promoCodeText})`}
                </span>
                <span className="discount-amount">
                  -{currencyFormatter(discountAmount)}
                </span>
              </div>
            )}

            <div className="totals-row total">
              <span>{t("total")}</span>
              <span>{currencyFormatter(selectedOrder.total)}</span>
            </div>
          </div>

          {/* Shipping Information */}
          {(selectedOrder.shippingInfo || selectedOrder.billingInfo) && (
            <div className="shipping-info-section">
              <h3>{t("shippingInformation")}</h3>
              <div className="shipping-card">
                <div className="shipping-row">
                  <span className="shipping-label">{t("customer")}</span>
                  <span className="shipping-value">
                    {getCustomerName(selectedOrder)}
                  </span>
                </div>
                <div className="shipping-row">
                  <span className="shipping-label">{t("address")}</span>
                  <span className="shipping-value">
                    {
                      (selectedOrder.shippingInfo || selectedOrder.billingInfo)
                        .city
                    }
                    ,{" "}
                    {
                      (selectedOrder.shippingInfo || selectedOrder.billingInfo)
                        .state
                    }{" "}
                    {
                      (selectedOrder.shippingInfo || selectedOrder.billingInfo)
                        .zipCode
                    }
                  </span>
                </div>
                <div className="shipping-row">
                  <span className="shipping-label">{t("country")}</span>
                  <span className="shipping-value">
                    {
                      (selectedOrder.shippingInfo || selectedOrder.billingInfo)
                        .country
                    }
                  </span>
                </div>
                <div className="shipping-row">
                  <span className="shipping-label">{t("phone")}</span>
                  <span className="shipping-value">
                    {(selectedOrder.shippingInfo || selectedOrder.billingInfo)
                      .phone || t("notAvailable")}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Order Notes */}
          {selectedOrder.orderNotes && (
            <div className="order-notes-section">
              <h3>{t("orderNotes")}</h3>
              <p>{selectedOrder.orderNotes}</p>
            </div>
          )}
        </div>

        <div className="sidebar-footer">
          {selectedOrder.invoice && (
            <button
              className="invoice-btn"
              onClick={() => window.open(selectedOrder.invoice, "_blank")}
            >
              {t("viewInvoice")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsSidebar;
